import React, { useState, useEffect } from 'react';
import '../Properties/property.scss';
import PropTypes from 'prop-types';
import SearchDropdown from '../../Components/Dropdown/dropdown';
import PropertyLayout from './layout';
import { fetchWaterSource, fetchRoadType } from '../../Api/request/propertyMasterApi';
import UseAxios from '../../Api/http/http';
import useApiState from '../../Hooks/useApiState';
import LoaderOverlay from '../../Components/Loader/LoaderOverlay';

const UNIT_OPTIONS = [
  { id: 'Sq Ft', name: 'Sq Ft' },
  { id: 'Cents', name: 'Cents' },
  { id: 'Acres', name: 'Acres' },
];
const BOUNDARY_TYPES = [
  { id: 'Fenced', name: 'Fenced' },
  { id: 'Walled', name: 'Walled' },
  { id: 'Open', name: 'Open' },
];
const SEWAGE_TYPES = [
  { id: 'Septic Tank', name: 'Septic Tank' },
  { id: 'Municipal', name: 'Municipal' },
  { id: 'Soak Pit', name: 'Soak Pit' },
];
const FACING_DIRECTIONS = [
  { id: 'N', name: 'N' },
  { id: 'S', name: 'S' },
  { id: 'E', name: 'E' },
  { id: 'W', name: 'W' },
  { id: 'NE', name: 'NE' },
  { id: 'SE', name: 'SE' },
  { id: 'NW', name: 'NW' },
  { id: 'SW', name: 'SW' },
];

const initialState = {
  extent: '',
  unit: UNIT_OPTIONS[0].id, // Store as string
  pattaExtent: '',
  kumkiExtent: '',
  boundaryType: '',
  roadAccess: true,
  roadType: '',
  accessRoadWidth: '',
  facing: [],
  waterSource: [],
  powerSupply: true,
  sanctionedCapacity: '',
  sewageLine: true,
  sewageType: '',
};

function LandDetails({ prevStep, onNext, onSave, onBackToDashboard, initialValues, step = 3, totalSteps = 10, systemId, isView = false }) {
  // Initialize form state with proper handling of decimal fields from initialValues
  const initializeFormState = () => {
    const baseState = { ...initialState };
    if (initialValues) {
      // Convert null values to empty strings for decimal fields to display properly in inputs
      const decimalFields = ['extent', 'pattaExtent', 'kumkiExtent', 'accessRoadWidth', 'sanctionedCapacity'];
      decimalFields.forEach(field => {
        if (initialValues[field] !== undefined) {
          baseState[field] = initialValues[field] === null ? '' : String(initialValues[field]);
        }
      });
      // Handle other fields normally
      Object.keys(initialValues).forEach(key => {
        if (!decimalFields.includes(key)) {
          baseState[key] = initialValues[key];
        }
      });
      // Ensure unit is handled properly
      if (typeof initialValues.unit === 'string') {
        baseState.unit = initialValues.unit;
      }
    }
    return baseState;
  };

  const [form, setForm] = useState(initializeFormState());
  const [WATER_SOURCES, setWaterSource] = useState([]);
  const [ROAD_TYPES, setRoadType] = useState([]);

  // Use the API state hook
  const {
    isLoading,
    isSubmitting,
    error,
    submitError,
    clearErrors,
    fetchData,
    submitData,
  } = useApiState({
    showToastOnError: true,
    defaultErrorMessage: 'Failed to process land details',
  });

  // Handle input changes
  const handleChange = (event) => {
    if (isView) return; // Prevent changes in view mode
    const { name, value, type, checked } = event.target;
    // For decimal fields, handle them specially
    const decimalFields = [
      'extent',
      'pattaExtent',
      'kumkiExtent',
      'accessRoadWidth',
      'sanctionedCapacity',
    ];
    if (decimalFields.includes(name)) {
      // Allow empty string or valid decimal numbers
      if (value === '' || /^\d*\.?\d*$/.test(value)) {
        setForm((prev) => ({ ...prev, [name]: value }));
      }
    } else {
      setForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    }
  };

  // Handle dropdowns
  const handleUnitChange = (event) => {
    if (isView) return; // Prevent changes in view mode
    // event.target.value is the selected object, so get its id
    const selected = event.target.value;
    setForm((prev) => ({ ...prev, unit: selected.id })); // Store as string
  };
  const handleBoundaryChange = (event) => {
    if (isView) return; // Prevent changes in view mode
    setForm((prev) => ({ ...prev, boundaryType: event.target.value.id }));
  };
  const handleRoadTypeChange = (event) => {
    if (isView) return; // Prevent changes in view mode
    setForm((prev) => ({ ...prev, roadType: event.target.value.name }));
  };
  const handleSewageTypeChange = (event) => {
    if (isView) return; // Prevent changes in view mode
    setForm((prev) => ({ ...prev, sewageType: event.target.value.id }));
  };

  // Toggle buttons
  const handleToggle = (name) => {
    if (isView) return; // Prevent changes in view mode
    setForm((prev) => ({ ...prev, [name]: !prev[name] }));
  };

  // Checkbox group
  const handleCheckboxGroup = (name, value) => {
    if (isView) return; // Prevent changes in view mode
    setForm((prev) => {
      const arr = prev[name];
      return {
        ...prev,
        [name]: arr.includes(value)
          ? arr.filter((event) => event !== value)
          : [...arr, value],
      };
    });
  };

  // Helper function to convert decimal fields to proper format
  const convertDecimalFields = (formData) => {
    return {
      ...formData,
      extent: formData.extent === '' || formData.extent === null ? null : parseFloat(formData.extent),
      pattaExtent: formData.pattaExtent === '' || formData.pattaExtent === null ? null : parseFloat(formData.pattaExtent),
      kumkiExtent: formData.kumkiExtent === '' || formData.kumkiExtent === null ? null : parseFloat(formData.kumkiExtent),
      accessRoadWidth: formData.accessRoadWidth === '' || formData.accessRoadWidth === null ? null : parseFloat(formData.accessRoadWidth),
      sanctionedCapacity: formData.sanctionedCapacity === '' || formData.sanctionedCapacity === null ? null : parseFloat(formData.sanctionedCapacity),
    };
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    clearErrors();

    try {
      const submitForm = convertDecimalFields(form);
      await submitData(async () => {
        return Promise.resolve(submitForm);
      }, {
        successMessage: 'Land details saved successfully',
      });

      onNext(submitForm);
    } catch (error) {
      console.error('Failed to save land details:', error);
    }
  };

  // Helper to save form data before navigating
  const handlePrev = () => {
    const formToSave = convertDecimalFields(form);
    onSave(formToSave); // Save form data with proper decimal conversion
    prevStep(); // Go to previous step
  };

  const fetchWaterSources = async () => {
    try {
      const response = await fetchData(() => UseAxios(fetchWaterSource()), {
        errorMessage: 'Failed to load water sources',
      });
      setWaterSource(response.data.waterSourceTypes || []);
      console.log('response here', response);
    } catch (error) {
      console.error('Failed to fetch water sources:', error);
    }
  };

  const fetchRoadTypes = async () => {
    try {
      const response = await fetchData(() => UseAxios(fetchRoadType()), {
        errorMessage: 'Failed to load road types',
      });
      setRoadType(response.data.roadTypes || []);
      console.log('response here', response);
    } catch (error) {
      console.error('Failed to fetch road types:', error);
    }
  };

  useEffect(() => {
    fetchWaterSources();
    fetchRoadTypes();
  }, []);

  return (
    <>
      <LoaderOverlay isVisible={isLoading} message="Loading land details..." />

      <PropertyLayout step={step} totalSteps={totalSteps} systemId={systemId} onBackToDashboard={onBackToDashboard}>
        <div className='card'>
          <h3 className='card-title'>Land Details</h3>

          {/* Display API errors */}
          {error && (
            <div className="error-message" style={{
              padding: '10px',
              marginBottom: '15px',
              backgroundColor: '#ffebee',
              color: '#c62828',
              borderRadius: '4px',
              border: '1px solid #ffcdd2',
            }}>
              {error}
            </div>
          )}

          {submitError && (
            <div className="error-message" style={{
              padding: '10px',
              marginBottom: '15px',
              backgroundColor: '#ffebee',
              color: '#c62828',
              borderRadius: '4px',
              border: '1px solid #ffcdd2',
            }}>
              {submitError}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {/* Row 1 */}
            <div className='form-row'>
              <div className='form-group'>
                <label>Extent of Land</label>
                <input
                  type='number'
                  step='0.01'
                  name='extent'
                  value={form.extent}
                  onChange={handleChange}
                  placeholder='e.g., 2400.50'
                  disabled={isSubmitting || isView}
                  readOnly={isView}
                />
              </div>
              <div className='form-group'>
                <SearchDropdown
                  label='Unit'
                  options={UNIT_OPTIONS}
                  value={UNIT_OPTIONS.find((unit) => unit.id === form.unit) || ''}
                  onChange={handleUnitChange}
                  placeholder='Sq Ft'
                  searchBox={false}
                  disabled={isSubmitting || isView}
                />
              </div>
            </div>
            <div className='form-row'>
              <div className='form-group'>
                <label>Patta Land Extent</label>
                <input
                  type='number'
                  step='0.01'
                  name='pattaExtent'
                  value={form.pattaExtent}
                  onChange={handleChange}
                  placeholder='e.g., 1500.25'
                  disabled={isSubmitting || isView}
                  readOnly={isView}
                />
                <div className='field-note'>Unit considered is same as extent of land ({form.unit})</div>
              </div>
              <div className='form-group'>
                <label>Kumki Land Extent</label>
                <input
                  type='number'
                  step='0.01'
                  name='kumkiExtent'
                  value={form.kumkiExtent}
                  onChange={handleChange}
                  placeholder='e.g., 900.75'
                  disabled={isSubmitting || isView}
                  readOnly={isView}
                />
                <div className='field-note'>Unit considered is same as extent of land ({form.unit})</div>
              </div>
            </div>

            {/* Row 2 */}
            <div className='form-row'>
              <div className='form-group' style={{ flex: 1, minWidth: 0 }}>
                <SearchDropdown
                  label='Boundary Type'
                  options={BOUNDARY_TYPES}
                  value={BOUNDARY_TYPES.find((boundary) => boundary.id === form.boundaryType) || ''}
                  onChange={handleBoundaryChange}
                  placeholder='Select boundary type'
                  searchBox={false}
                  style={{ width: '100%' }}
                  disabled={isSubmitting || isView}
                />
              </div>
              <div className='form-group' style={{ flex: 1, minWidth: 0, display: 'flex' }}>
                <label style={{ marginRight: 16, minWidth: 90 }}>Road Access</label>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <label className="switch">
                    <input type="checkbox" checked={form.roadAccess} onChange={() => handleToggle('roadAccess')} disabled={isView} />
                    <span className="slider round"></span>
                  </label>
                  <span style={{ marginLeft: 12 }}>{form.roadAccess ? 'Available' : 'Not Available'}</span>
                </div>
              </div>
            </div>

            {/* Row 3 - Conditional */}
            <div className='form-row' style={{ opacity: form.roadAccess ? 1 : 0.5, pointerEvents: form.roadAccess ? 'auto' : 'none' }}>
              <div className='form-group'>
                <SearchDropdown
                  label='Road Type'
                  options={ROAD_TYPES}
                  value={ROAD_TYPES.find((road) => road.name === form.roadType) || ''}
                  onChange={handleRoadTypeChange}
                  placeholder='Select road type'
                  searchBox={false}
                  disabled={!form.roadAccess || isSubmitting || isView}
                />
              </div>
              <div className='form-group'>
                <label>Access Road Width (meters)</label>
                <input
                  type='number'
                  step='0.01'
                  name='accessRoadWidth'
                  value={form.accessRoadWidth}
                  onChange={handleChange}
                  placeholder='e.g., 12.5'
                  disabled={!form.roadAccess || isSubmitting || isView}
                  readOnly={isView}
                />
              </div>
            </div>

            {/* Row 4 - Site Facing Direction & Water Source in a single row */}
            <div className='form-row'>
              <div className='form-group' style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <label className='input-header-label'>Site Facing Direction</label>
                <div className='checkbox-group' style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
                  {FACING_DIRECTIONS.map((dir) => (
                    <label key={dir.id} className='checkbox-inline' style={{ display: 'flex', alignItems: 'center', minWidth: 60 }}>
                      <input
                        type='checkbox'
                        checked={form.facing.includes(dir.id)}
                        onChange={() => handleCheckboxGroup('facing', dir.id)}
                        disabled={isView}
                      />
                      {dir.name}
                    </label>
                  ))}
                </div>
              </div>
              <div className='form-group' style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <label className='input-header-label'>Water Source</label>
                <div className='checkbox-group' style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
                  {WATER_SOURCES.map((src) => (
                    <label key={src.id} className='checkbox-inline' style={{ display: 'flex', alignItems: 'center', minWidth: 80 }}>
                      <input
                        type='checkbox'
                        checked={form.waterSource.includes(src.name)}
                        onChange={() => handleCheckboxGroup('waterSource', src.name)}
                        disabled={isView}
                      />
                      {src.name}
                    </label>
                  ))}
                </div>
              </div>
            </div>

            {/* Row 6 & 7 - Power Supply & Sewage Line Combined */}
            <div className='form-row'>
              <div className='form-group' style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <label style={{ marginBottom: 8, fontWeight: 700 }}>Power Supply</label>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <label className="switch">
                    <input type="checkbox" checked={form.powerSupply} onChange={() => handleToggle('powerSupply')} disabled={isView} />
                    <span className="slider round"></span>
                  </label>
                  <span style={{ marginLeft: 12 }}>{form.powerSupply ? 'Available' : 'Not Available'}</span>
                </div>
              </div>
              <div className='form-group' style={{ flex: 1, minWidth: 0 }}>
                <label>Sanctioned Capacity (KW)</label>
                <input
                  type='number'
                  step='0.01'
                  name='sanctionedCapacity'
                  value={form.sanctionedCapacity}
                  onChange={handleChange}
                  placeholder='e.g., 5.5'
                  disabled={!form.powerSupply || isSubmitting || isView}
                  readOnly={isView}
                />
              </div>
              <div className='form-group' style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <label style={{ marginBottom: 8, fontWeight: 700 }}>Sewage Line</label>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <label className="switch">
                    <input type="checkbox" checked={form.sewageLine} onChange={() => handleToggle('sewageLine')} disabled={isView} />
                    <span className="slider round"></span>
                  </label>
                  <span style={{ marginLeft: 12 }}>{form.sewageLine ? 'Available' : 'Not Available'}</span>
                </div>
              </div>
              <div className='form-group' style={{ flex: 1, minWidth: 0 }}>
                <SearchDropdown
                  label='Sewage Type'
                  options={SEWAGE_TYPES}
                  value={SEWAGE_TYPES.find((sewage) => sewage.id === form.sewageType) || ''}
                  onChange={handleSewageTypeChange}
                  placeholder='Select sewage type'
                  searchBox={false}
                  disabled={!form.sewageLine || isSubmitting || isView}
                />
              </div>
            </div>

            <div className='form-actions'>
              <button
                type='button'
                className='btn secondary'
                onClick={handlePrev}
                disabled={isSubmitting}
              >
                Previous
              </button>
              {isView ? (
                <button
                  type='button'
                  className='btn primary'
                  onClick={() => onNext(convertDecimalFields(form))}
                >
                  Next
                </button>
              ) : (
                <button
                  type='submit'
                  className='btn primary'
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Saving...' : 'Next'}
                </button>
              )}
            </div>
          </form>
        </div>
      </PropertyLayout>
    </>
  );
}

LandDetails.propTypes = {
  prevStep: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onBackToDashboard: PropTypes.func,
  initialValues: PropTypes.object,
  step: PropTypes.number,
  totalSteps: PropTypes.number,
  systemId: PropTypes.string,
  isView: PropTypes.bool,
};

export default LandDetails;
